import React, { useEffect, useState } from 'react'
import { Button, OnboardTitle, OnboardSubtitle } from 'components/Antifraude'
import { Box, InputAdornment, TextField } from '@mui/material'
import {
  VisibilityOutlined,
  VisibilityOffOutlined,
  PrivacyTip,
  LockOutlined,
} from '@mui/icons-material/'
import cloneDeep from 'lodash/cloneDeep'
import { toast } from 'react-toastify'
import { ValidatePassword } from 'utils/validators'
import { ROUTE_CONSTANTS } from 'utils/constants/routeConstants'
import { submitNewPassword } from 'services/apiKYP'
import { useSelector } from 'react-redux'
import jwtDecode from 'jwt-decode'
import { gotoUrl } from 'utils/functions'

import regexes from './regex'

import styles from './styles'

const getTokenData = (token) => {
  if (token) {
    return jwtDecode(token)
  }
  return { name: 'John Doe', email: 'mail@example.com' }
}

const PasswordCreation = (props) => {
  const { location } = props
  const { token } = useSelector((state) => state.token)
  const classes = styles()

  const { extras } = getTokenData(token)
  const name = extras.name.split(' ')[0]

  const [formValues, setFormValues] = useState({
    password: '',
    password_confirmation: '',
  })
  const [formErrors, setFormErrors] = useState({
    password: false,
    password_confirmation: false,
  })

  const [loading, setLoading] = useState(false)

  const [passwordOperationType, setPasswordOperationType] = useState()

  const [isPasswordsValid, setIsPasswordValid] = useState(false)

  const [passwordRequirements, setPasswordRequirements] = useState({
    uppercaseLetter: false,
    downcaseLetter: false,
    specialCharacter: false,
    number: false,
    stringSize: false,
    passwordMatch: false,
  })

  const [showPassword, setShowPassword] = useState(false)

  const handlePasswordValidation = () => {
    return ValidatePassword(formValues.password)
  }

  const handleConfirmPasswordValidation = () => {
    return (
      ValidatePassword(formValues.password_confirmation) &&
      formValues.password_confirmation === formValues.password
    )
  }

  const isAllRequirementsFullFilled = () => {
    return Object.values(passwordRequirements).every((value) => value)
  }

  const validateFields = () => {
    const errors = cloneDeep(formErrors)
    const allRequimentsFullfilled = isAllRequirementsFullFilled()
    if (allRequimentsFullfilled) {
      errors.password = false
      errors.password_confirmation = false
      setFormErrors(errors)
      return {
        isFieldsValid: true,
        errors: errors,
      }
    }

    const actions = {
      password: handlePasswordValidation,
      password_confirmation: handleConfirmPasswordValidation,
    }

    Object.keys(formValues).forEach((key) => {
      errors[key] = !actions[key](formValues[key])
    })
    setFormErrors(errors)
    return {
      isFieldsValid: !Object.keys(errors).some((key) => errors[key]),
      errors: errors,
    }
  }

  const getInvalidFieldNames = (errors) => {
    const names = {
      password: 'Senha',
      password_confirmation: 'Confirmação de Senha',
    }
    const fields = Object.keys(errors).filter((key) => errors[key])

    const fieldNames = fields.map((field) => names[field])

    return fieldNames.join(' e ')
  }

  const handleSubmit = async () => {
    const data = cloneDeep(formValues)

    setLoading(true)

    submitNewPassword(token, passwordOperationType, data)
      .then((response) => {
        if (response.status === 204) gotoUrl(ROUTE_CONSTANTS.PASSWORD_SUCCESS)
      })
      .catch((error) => {
        toast.error(`Erro: ${error?.response?.data?.detail}`)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleSendPassword = () => {
    const { isFieldsValid, errors } = validateFields()
    const errorsNumber = Object.keys(errors).filter(
      (error) => errors[error],
    ).length
    if (isFieldsValid) {
      handleSubmit()
    } else {
      toast.error(
        `Verifique se a senha digitada no${errorsNumber > 1 ? 's' : ''} campo${
          errorsNumber > 1 ? 's' : ''
        } ${getInvalidFieldNames(
          errors,
        )} está de acordo com os requisitos informados.`,
      )
    }
  }

  const checkRequirements = (value, key) => {
    const passwd_req = cloneDeep(passwordRequirements)
    if (key === 'password') {
      passwd_req.uppercaseLetter = regexes.uppercase.test(value)
      passwd_req.downcaseLetter = regexes.downcase.test(value)
      passwd_req.number = regexes.number.test(value)
      passwd_req.specialCharacter = regexes.specialChar.test(value)
      passwd_req.stringSize = value.length >= 10 && value.length <= 18
    }
    passwd_req.passwordMatch =
      key === 'password'
        ? value === formValues.password_confirmation
        : value === formValues.password

    setPasswordRequirements(passwd_req)
  }

  const handleChange = (event) => {
    const key = event.target.name
    const currentValue = cloneDeep(formValues)
    const { value } = event.target

    checkRequirements(value, key)

    currentValue[key] = value
    setFormValues(currentValue)
  }

  useEffect(() => {
    const type =
      location.pathname === ROUTE_CONSTANTS.PASSWORD_REDEFINE
        ? 'redefine'
        : 'creation'
    setPasswordOperationType(type)
  }, [location.pathname])

  useEffect(() => {
    setIsPasswordValid(isAllRequirementsFullFilled)
  }, [formValues.password_confirmation, formValues.password])

  return (
    <div className={classes.container}>
      <div className={classes.titleBar}>
        <div className={classes.title}>
          <OnboardTitle>
            {passwordOperationType === 'creation'
              ? 'Cadastro '
              : 'Recuperação '}
            de senha do Parceiro XP{' '}
          </OnboardTitle>
          <div className={classes.locker}>
            <LockOutlined fontSize="inherit" />
          </div>
        </div>
        <OnboardSubtitle>
          {`${name}, crie ${
            passwordOperationType === 'creation'
              ? 'uma senha de acesso ao sistema para realização das próximas etapas'
              : 'sua nova senha de acesso ao sistema'
          }`}
        </OnboardSubtitle>
      </div>
      <div className={classes.instructions}>
        <div>
          <span>Requisitos mínimos para a criação da senha: </span>
          <ul>
            <li>
              <PrivacyTip
                className={
                  passwordRequirements.uppercaseLetter
                    ? classes.success
                    : classes.waiting
                }
              />{' '}
              Ao menos uma letra maiúscula
            </li>
            <li>
              <PrivacyTip
                className={
                  passwordRequirements.downcaseLetter
                    ? classes.success
                    : classes.waiting
                }
              />{' '}
              Ao menos uma letra minúscula
            </li>
            <li>
              <PrivacyTip
                className={
                  passwordRequirements.number
                    ? classes.success
                    : classes.waiting
                }
              />{' '}
              Ao menos um caractere numérico
            </li>
            <li>
              <PrivacyTip
                className={
                  passwordRequirements.specialCharacter
                    ? classes.success
                    : classes.waiting
                }
              />{' '}
              Ao menos um caractere especial (@#$!%*?&){' '}
            </li>
            <li>
              <PrivacyTip
                className={
                  passwordRequirements.stringSize
                    ? classes.success
                    : classes.waiting
                }
              />{' '}
              Tamanho entre 10 e 18 dígitos
            </li>
            <li>
              <PrivacyTip
                className={
                  passwordRequirements.passwordMatch
                    ? classes.success
                    : classes.waiting
                }
              />{' '}
              As senhas devem ser iguais
            </li>
          </ul>
        </div>
      </div>
      <Box
        component="form"
        className={classes.formBox}
        noValidate
        autoComplete="on"
      >
        <TextField
          required
          fullWidth
          autoComplete="new-password"
          error={formErrors.password}
          name="password"
          label="Senha"
          value={formValues.password}
          onChange={handleChange}
          type={showPassword ? 'text' : 'password'}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="start"
                onClick={() => {
                  setShowPassword(!showPassword)
                }}
              >
                {showPassword ? (
                  <VisibilityOutlined />
                ) : (
                  <VisibilityOffOutlined />
                )}
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          fullWidth
          error={formErrors.password_confirmation}
          name="password_confirmation"
          label="Confirme sua senha"
          value={formValues.password_confirmation}
          onChange={handleChange}
          type={showPassword ? 'text' : 'password'}
          size="small"
        />
        <Button
          loading={loading}
          onClick={handleSendPassword}
          disabled={!isPasswordsValid}
        >
          {passwordOperationType === 'creation'
            ? 'Criar Conta'
            : 'Alterar Senha'}
        </Button>
      </Box>
    </div>
  )
}

export default PasswordCreation
